<template>
  <div class="page-content">
    <breadcrumb-index :items="breadcrumbs" />

    <div class="row g-3">
      <div class="col-lg-3">
        <div class="card">
          <div class="card-body py-2">
            <ul class="nav nav-pills flex-column">
              <li class="nav-item">
                <router-link to="/app/settings/profile" class="nav-link" active-class="active">
                  <vue-feather type="user" size="16" class="me-2" />
                  Profile
                </router-link>
              </li>
              <li class="nav-item">
                <router-link to="/app/settings/security" class="nav-link" active-class="active">
                  <vue-feather type="shield" size="16" class="me-2" />
                  Security
                </router-link>
              </li>
              <li class="nav-item" v-if="isSuperAdmin">
                <router-link to="/app/settings/app" class="nav-link" active-class="active">
                  <vue-feather type="settings" size="16" class="me-2" />
                  App Settings
                </router-link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="col-lg-9">
        <!-- Change Password -->
        <div class="card mb-3">
          <div class="card-header">
            <h6 class="card-title mb-0">Change Password</h6>
          </div>
          <div class="card-body">
            <form @submit.prevent="handlePasswordChange">
              <div class="row g-3">
                <div class="col-md-4">
                  <label class="form-label">Current Password</label>
                  <input v-model="passwordForm.currentPassword" type="password" class="form-control" required />
                </div>
                <div class="col-md-4">
                  <label class="form-label">New Password</label>
                  <input v-model="passwordForm.newPassword" type="password" class="form-control" required minlength="8" />
                </div>
                <div class="col-md-4">
                  <label class="form-label">Confirm Password</label>
                  <input v-model="passwordForm.confirmPassword" type="password" class="form-control" required />
                </div>
              </div>

              <div v-if="passwordError" class="alert alert-danger mt-3 mb-0">{{ passwordError }}</div>
              <div v-if="passwordSuccess" class="alert alert-success mt-3 mb-0">Password changed successfully!</div>

              <button type="submit" class="btn btn-primary btn-sm mt-3" :disabled="passwordLoading">
                <span v-if="passwordLoading" class="spinner-border spinner-border-sm me-2"></span>
                Change Password
              </button>
            </form>
          </div>
        </div>

        <!-- Two-Factor Authentication -->
        <div class="card">
          <div class="card-header">
            <h6 class="card-title mb-0">Two-Factor Authentication</h6>
          </div>
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="mb-1 small fw-medium">Email-based 2FA</h6>
                <p class="text-muted small mb-0">
                  Receive a verification code via email when signing in
                </p>
              </div>
              <div class="form-check form-switch">
                <input
                  v-model="twoFactorEnabled"
                  type="checkbox"
                  class="form-check-input"
                  @change="toggle2FA"
                />
              </div>
            </div>

            <hr />

            <div>
              <h6 class="mb-2 small fw-medium">Active Sessions</h6>
              <p class="text-muted small mb-2">
                You're currently logged in on this device. For security, you can log out of all other sessions.
              </p>
              <button class="btn btn-outline-danger btn-sm" @click="logoutAllSessions">
                Log Out All Other Sessions
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import api from '@/services/api'
import { useAlert } from '@/plugins/sweetalert'

export default {
  name: 'SecuritySettings',
  setup() {
    const authStore = useAuthStore()
    const alert = useAlert()
    const isSuperAdmin = computed(() => authStore.isSuperAdmin)

    const breadcrumbs = [
      { title: 'Dashboard', url: '/app/dashboard' },
      { title: 'Settings' },
      { title: 'Security', active: true }
    ]

    const passwordForm = ref({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    })
    const passwordLoading = ref(false)
    const passwordError = ref('')
    const passwordSuccess = ref(false)

    const twoFactorEnabled = ref(false)

    onMounted(() => {
      twoFactorEnabled.value = authStore.user?.twoFactorEnabled || false
    })

    const handlePasswordChange = async () => {
      if (passwordForm.value.newPassword !== passwordForm.value.confirmPassword) {
        passwordError.value = 'Passwords do not match'
        return
      }

      passwordLoading.value = true
      passwordError.value = ''
      passwordSuccess.value = false

      try {
        await api.put('/v1/admin/settings/security', {
          currentPassword: passwordForm.value.currentPassword,
          newPassword: passwordForm.value.newPassword
        })
        passwordSuccess.value = true
        passwordForm.value = { currentPassword: '', newPassword: '', confirmPassword: '' }
      } catch (err) {
        passwordError.value = err.response?.data?.message || 'Failed to change password'
      } finally {
        passwordLoading.value = false
      }
    }

    const toggle2FA = async () => {
      try {
        await api.put('/v1/admin/settings/security', { twoFactorEnabled: twoFactorEnabled.value })
        alert.success(
          'Success',
          `Two-factor authentication ${twoFactorEnabled.value ? 'enabled' : 'disabled'}`
        )
      } catch (err) {
        twoFactorEnabled.value = !twoFactorEnabled.value
        alert.error('Error', 'Failed to update 2FA settings')
      }
    }

    const logoutAllSessions = async () => {
      const result = await alert.confirm(
        'Log Out All Sessions?',
        'This will log you out from all other devices.'
      )

      if (result.isConfirmed) {
        try {
          await api.post('/v1/admin/settings/security/logout-all')
          alert.success('Success', 'All other sessions have been logged out')
        } catch (err) {
          alert.error('Error', 'Failed to log out sessions')
        }
      }
    }

    return {
      breadcrumbs,
      isSuperAdmin,
      passwordForm,
      passwordLoading,
      passwordError,
      passwordSuccess,
      twoFactorEnabled,
      handlePasswordChange,
      toggle2FA,
      logoutAllSessions
    }
  }
}
</script>
